// v10/src/components/ChecklistEditor.js - REDESIGNED + REQUIRES_PHOTO
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit3, Trash2, Plus, Check, Camera, X, Save, ChevronDown, ChevronUp, GripVertical } from 'lucide-react';

const ChecklistEditor = ({ aircraft, onSave, onCancel, darkMode }) => {
  const [editedChecklist, setEditedChecklist] = useState(
    JSON.parse(JSON.stringify(aircraft.checklist))
  );
  const [editingSection, setEditingSection] = useState(null);
  const [newSectionName, setNewSectionName] = useState('');
  const [collapsedSections, setCollapsedSections] = useState({});

  // ── Section ops ───────────────────────────────────────────────
  const addSection = () => {
    if (newSectionName.trim()) {
      setEditedChecklist([...editedChecklist, { section: newSectionName, items: [] }]);
      setNewSectionName('');
    }
  };
  const removeSection  = (idx) => setEditedChecklist(editedChecklist.filter((_, i) => i !== idx));
  const updateSectionName = (idx, value) => {
    const n = [...editedChecklist]; n[idx].section = value; setEditedChecklist(n);
  };
  const toggleSection  = (idx) => setCollapsedSections(p => ({ ...p, [idx]: !p[idx] }));

  // ── Item ops ──────────────────────────────────────────────────
  const addItem = (sIdx) => {
    const n = [...editedChecklist];
    n[sIdx].items.push({ text: 'Nuevo ítem', referenceImage: null, description: '', requires_photo: false });
    setEditedChecklist(n);
  };
  const removeItem = (sIdx, iIdx) => {
    const n = [...editedChecklist]; n[sIdx].items.splice(iIdx, 1); setEditedChecklist(n);
  };
  const updateItem = (sIdx, iIdx, field, value) => {
    const n = [...editedChecklist];
    if (typeof n[sIdx].items[iIdx] === 'string') {
      n[sIdx].items[iIdx] = { text: n[sIdx].items[iIdx], referenceImage: null, description: '', requires_photo: false };
    }
    n[sIdx].items[iIdx][field] = value;
    setEditedChecklist(n);
  };
  const handleImageUpload = (sIdx, iIdx, e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => updateItem(sIdx, iIdx, 'referenceImage', ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  // ── Style helpers ─────────────────────────────────────────────
  const card        = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)';
  const border      = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(37,99,235,0.1)';
  const inputBg     = darkMode ? 'rgba(255,255,255,0.06)' : '#fff';
  const inputBorder = darkMode ? 'rgba(255,255,255,0.1)'  : 'rgba(37,99,235,0.15)';
  const text        = darkMode ? 'rgba(255,255,255,0.88)' : '#0f172a';
  const sub         = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';

  const inputStyle = {
    background: inputBg, border: `1.5px solid ${inputBorder}`,
    borderRadius: '12px', padding: '10px 14px',
    fontSize: '14px', fontWeight: 500, color: text,
    width: '100%', outline: 'none', transition: 'border-color 0.18s',
    fontFamily: 'inherit',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '16px', overflowY: 'auto',
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
    }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 140, damping: 18 }}
        style={{
          width: '100%', maxWidth: '720px',
          background: darkMode
            ? 'linear-gradient(160deg,#0d1220,#111827)'
            : 'linear-gradient(160deg,#f0f4ff,#f8fafc)',
          borderRadius: '28px',
          boxShadow: '0 24px 80px rgba(0,0,0,0.25), 0 0 0 1px rgba(37,99,235,0.1)',
          overflow: 'hidden',
          marginTop: '8px', marginBottom: '24px',
        }}
      >
        {/* ── Top bar ── */}
        <div style={{
          padding: '20px 24px 18px',
          background: darkMode
            ? 'linear-gradient(135deg,rgba(37,99,235,0.15),rgba(109,40,217,0.12))'
            : 'linear-gradient(135deg,rgba(37,99,235,0.08),rgba(109,40,217,0.06))',
          borderBottom: `1px solid ${border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '42px', height: '42px', borderRadius: '13px',
              background: 'linear-gradient(135deg,#2563eb,#7c3aed)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(99,60,220,0.3)', fontSize: '20px',
            }}>✏️</div>
            <div>
              <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: text, letterSpacing: '-0.3px' }}>
                Editar Checklist
              </h2>
              <p style={{ margin: 0, fontSize: '12px', color: sub, fontWeight: 500 }}>
                {aircraft.name} · {editedChecklist.length} secciones
              </p>
            </div>
          </div>
          <motion.button
            onClick={onCancel}
            whileHover={{ scale: 1.1, rotate: 90 }} whileTap={{ scale: 0.9 }}
            style={{
              width: '34px', height: '34px', borderRadius: '10px', border: 'none',
              background: darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: sub,
            }}
          >
            <X style={{ width: '16px', height: '16px' }} />
          </motion.button>
        </div>

        {/* ── Scrollable content ── */}
        <div style={{ padding: '20px 24px', maxHeight: 'calc(90vh - 140px)', overflowY: 'auto' }}>

          <AnimatePresence>
            {editedChecklist.map((section, sIdx) => (
              <motion.div
                key={sIdx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10, scale: 0.97 }}
                transition={{ delay: sIdx * 0.04 }}
                style={{
                  marginBottom: '16px', borderRadius: '20px', overflow: 'hidden',
                  background: card, border: `1.5px solid ${border}`,
                  boxShadow: darkMode ? 'none' : '0 2px 12px rgba(37,99,235,0.06)',
                }}
              >
                {/* Section header */}
                <div style={{
                  padding: '14px 16px',
                  background: darkMode
                    ? 'rgba(37,99,235,0.1)'
                    : 'linear-gradient(135deg,rgba(37,99,235,0.07),rgba(109,40,217,0.04))',
                  borderBottom: `1px solid ${border}`,
                  display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                  <GripVertical style={{ width: '14px', height: '14px', color: sub, flexShrink: 0, opacity: 0.5 }} />

                  {editingSection === sIdx ? (
                    <>
                      <input
                        autoFocus type="text"
                        value={section.section}
                        onChange={e => updateSectionName(sIdx, e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && setEditingSection(null)}
                        style={{ ...inputStyle, flex: 1, padding: '8px 12px', fontSize: '14px', fontWeight: 700 }}
                      />
                      <motion.button
                        onClick={() => setEditingSection(null)} whileTap={{ scale: 0.9 }}
                        style={{ width: '30px', height: '30px', borderRadius: '9px', border: 'none', background: 'rgba(34,197,94,0.15)', color: '#22c55e', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                      >
                        <Check style={{ width: '14px', height: '14px' }} />
                      </motion.button>
                    </>
                  ) : (
                    <>
                      <h3 style={{ flex: 1, margin: 0, fontSize: '14px', fontWeight: 800, color: text, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                        {section.section}
                      </h3>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: sub, background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(37,99,235,0.08)', padding: '2px 8px', borderRadius: '20px' }}>
                        {section.items.length} ítems
                      </span>
                      <motion.button onClick={() => setEditingSection(sIdx)} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                        style={{ width: '28px', height: '28px', borderRadius: '8px', border: 'none', background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(37,99,235,0.08)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: darkMode ? 'rgba(147,197,253,0.8)' : '#2563eb' }}>
                        <Edit3 style={{ width: '13px', height: '13px' }} />
                      </motion.button>
                      <motion.button onClick={() => removeSection(sIdx)} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                        style={{ width: '28px', height: '28px', borderRadius: '8px', border: 'none', background: 'rgba(239,68,68,0.08)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>
                        <Trash2 style={{ width: '13px', height: '13px' }} />
                      </motion.button>
                      <motion.button onClick={() => toggleSection(sIdx)} whileTap={{ scale: 0.9 }}
                        style={{ width: '28px', height: '28px', borderRadius: '8px', border: 'none', background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: sub }}>
                        {collapsedSections[sIdx]
                          ? <ChevronDown style={{ width: '14px', height: '14px' }} />
                          : <ChevronUp   style={{ width: '14px', height: '14px' }} />
                        }
                      </motion.button>
                    </>
                  )}
                </div>

                {/* Items */}
                <AnimatePresence>
                  {!collapsedSections[sIdx] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22 }}
                      style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}
                    >
                      {section.items.map((item, iIdx) => {
                        const obj = typeof item === 'string'
                          ? { text: item, referenceImage: null, description: '', requires_photo: false }
                          : item;
                        const requiresPhoto = obj.requires_photo === true;

                        return (
                          <motion.div
                            key={iIdx}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 8, scale: 0.97 }}
                            transition={{ delay: iIdx * 0.03 }}
                            style={{
                              borderRadius: '14px', overflow: 'hidden',
                              background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(248,250,255,0.8)',
                              border: requiresPhoto
                                ? `1px solid ${darkMode ? 'rgba(249,115,22,0.35)' : 'rgba(249,115,22,0.3)'}`
                                : `1px solid ${darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(37,99,235,0.08)'}`,
                            }}
                          >
                            {/* Item top: number + text + delete */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px 8px' }}>
                              <span style={{
                                width: '24px', height: '24px', borderRadius: '8px', flexShrink: 0,
                                background: 'linear-gradient(135deg,#2563eb,#7c3aed)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '10px', fontWeight: 800, color: 'white',
                              }}>{iIdx + 1}</span>
                              <input
                                type="text"
                                value={obj.text}
                                onChange={e => updateItem(sIdx, iIdx, 'text', e.target.value)}
                                placeholder="Texto del ítem"
                                style={{ ...inputStyle, flex: 1, fontWeight: 600, padding: '8px 12px' }}
                              />
                              <motion.button onClick={() => removeItem(sIdx, iIdx)} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                                style={{ width: '28px', height: '28px', borderRadius: '8px', border: 'none', background: 'rgba(239,68,68,0.08)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444', flexShrink: 0 }}>
                                <Trash2 style={{ width: '13px', height: '13px' }} />
                              </motion.button>
                            </div>

                            {/* Description */}
                            <div style={{ padding: '0 12px 10px' }}>
                              <textarea
                                value={obj.description || ''}
                                onChange={e => updateItem(sIdx, iIdx, 'description', e.target.value)}
                                placeholder="Descripción o instrucciones detalladas..."
                                rows={2}
                                style={{ ...inputStyle, resize: 'vertical', fontSize: '13px' }}
                              />
                            </div>

                            {/* ✅ NUEVO: Toggle "Foto obligatoria" */}
                            <div style={{
                              margin: '0 12px 12px',
                              padding: '10px 14px',
                              borderRadius: '12px',
                              background: requiresPhoto
                                ? darkMode ? 'rgba(249,115,22,0.12)' : 'rgba(249,115,22,0.08)'
                                : darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                              border: `1px solid ${requiresPhoto
                                ? darkMode ? 'rgba(249,115,22,0.3)' : 'rgba(249,115,22,0.25)'
                                : darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                            }}
                              onClick={() => updateItem(sIdx, iIdx, 'requires_photo', !requiresPhoto)}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Camera style={{
                                  width: '15px', height: '15px', flexShrink: 0,
                                  color: requiresPhoto ? '#f97316' : sub,
                                  transition: 'color 0.2s',
                                }} />
                                <div>
                                  <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: requiresPhoto ? (darkMode ? '#fb923c' : '#ea580c') : text, transition: 'color 0.2s' }}>
                                    Foto obligatoria
                                  </p>
                                  <p style={{ margin: 0, fontSize: '11px', color: sub }}>
                                    El piloto debe adjuntar una foto antes de chequear
                                  </p>
                                </div>
                              </div>

                              {/* Switch */}
                              <motion.div
                                animate={{ background: requiresPhoto ? '#f97316' : (darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)') }}
                                transition={{ duration: 0.2 }}
                                style={{
                                  width: '42px', height: '24px', borderRadius: '12px',
                                  flexShrink: 0, position: 'relative', cursor: 'pointer',
                                }}
                              >
                                <motion.div
                                  animate={{ x: requiresPhoto ? 20 : 2 }}
                                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                  style={{
                                    position: 'absolute', top: '3px',
                                    width: '18px', height: '18px', borderRadius: '50%',
                                    background: 'white',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                                  }}
                                />
                              </motion.div>
                            </div>

                            {/* Reference image */}
                            <div style={{
                              margin: '0 12px 12px',
                              borderRadius: '12px', overflow: 'hidden',
                              border: `1.5px dashed ${darkMode ? 'rgba(37,99,235,0.25)' : 'rgba(37,99,235,0.2)'}`,
                              background: darkMode ? 'rgba(37,99,235,0.04)' : 'rgba(37,99,235,0.03)',
                            }}>
                              <div style={{ padding: '8px 12px 6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '11px', fontWeight: 700, color: darkMode ? 'rgba(147,197,253,0.7)' : '#2563eb', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'monospace' }}>
                                  🖼 Imagen de referencia
                                </span>
                                <span style={{ fontSize: '10px', color: sub }}>(opcional)</span>
                              </div>

                              {obj.referenceImage ? (
                                <div style={{ position: 'relative', margin: '0 10px 10px' }}>
                                  <img
                                    src={obj.referenceImage} alt="Reference"
                                    style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: '10px', display: 'block' }}
                                  />
                                  <div style={{
                                    position: 'absolute', inset: 0, borderRadius: '10px',
                                    background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 50%)',
                                    display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
                                    padding: '8px 10px',
                                  }}>
                                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>Click para cambiar</span>
                                    <motion.button
                                      onClick={() => updateItem(sIdx, iIdx, 'referenceImage', null)}
                                      whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                                      style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(239,68,68,0.85)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}
                                    >
                                      <X style={{ width: '12px', height: '12px' }} />
                                    </motion.button>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '14px 12px 8px', gap: '4px' }}>
                                  <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: darkMode ? 'rgba(37,99,235,0.1)' : 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Camera style={{ width: '18px', height: '18px', color: darkMode ? 'rgba(147,197,253,0.6)' : '#3b82f6' }} />
                                  </div>
                                  <p style={{ margin: 0, fontSize: '12px', color: sub }}>Sin imagen de referencia</p>
                                </div>
                              )}

                              <label style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                margin: '6px 10px 10px', padding: '10px',
                                background: 'linear-gradient(135deg,#2563eb,#6d28d9)',
                                color: 'white', borderRadius: '10px',
                                fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                                boxShadow: '0 3px 10px rgba(37,99,235,0.3)',
                                transition: 'opacity 0.15s',
                              }}>
                                <input type="file" accept="image/*" style={{ display: 'none' }}
                                  onChange={e => handleImageUpload(sIdx, iIdx, e)} />
                                <Camera style={{ width: '15px', height: '15px' }} />
                                {obj.referenceImage ? 'Cambiar imagen' : 'Subir imagen'}
                              </label>
                            </div>
                          </motion.div>
                        );
                      })}

                      {/* Add item button */}
                      <motion.button
                        onClick={() => addItem(sIdx)}
                        whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
                        style={{
                          width: '100%', padding: '11px', background: 'transparent',
                          border: `1.5px dashed ${darkMode ? 'rgba(37,99,235,0.3)' : 'rgba(37,99,235,0.25)'}`,
                          borderRadius: '12px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
                          fontSize: '13px', fontWeight: 700,
                          color: darkMode ? 'rgba(147,197,253,0.7)' : '#2563eb',
                          transition: 'background 0.18s',
                        }}
                      >
                        <Plus style={{ width: '15px', height: '15px' }} />
                        Agregar ítem
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* ── Add section ── */}
          <div style={{
            display: 'flex', gap: '10px', marginBottom: '20px',
            padding: '14px', borderRadius: '16px',
            background: card, border: `1.5px dashed ${border}`,
          }}>
            <input
              type="text"
              placeholder="Nombre de la nueva sección..."
              value={newSectionName}
              onChange={e => setNewSectionName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addSection()}
              style={{ ...inputStyle, flex: 1 }}
            />
            <motion.button
              onClick={addSection}
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              style={{
                width: '46px', height: '46px', borderRadius: '12px', border: 'none',
                background: 'linear-gradient(135deg,#2563eb,#6d28d9)',
                color: 'white', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 14px rgba(37,99,235,0.3)', flexShrink: 0,
              }}
            >
              <Plus style={{ width: '20px', height: '20px' }} />
            </motion.button>
          </div>

          {/* ── Action buttons ── */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <motion.button
              onClick={() => onSave({ ...aircraft, checklist: editedChecklist })}
              whileHover={{ scale: 1.02, y: -1 }} whileTap={{ scale: 0.97 }}
              style={{
                flex: 1, padding: '15px',
                background: 'linear-gradient(135deg,#16a34a,#15803d)',
                color: 'white', border: 'none', borderRadius: '16px',
                fontWeight: 800, fontSize: '15px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                boxShadow: '0 6px 20px rgba(22,163,74,0.3)',
              }}
            >
              <Save style={{ width: '17px', height: '17px' }} />
              Guardar Cambios
            </motion.button>
            <motion.button
              onClick={onCancel}
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
              style={{
                flex: 1, padding: '15px',
                background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.05)',
                color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.55)',
                border: `1.5px solid ${border}`,
                borderRadius: '16px', fontWeight: 700, fontSize: '15px', cursor: 'pointer',
              }}
            >
              Cancelar
            </motion.button>
          </div>

        </div>
      </motion.div>
    </div>
  );
};

export default ChecklistEditor;
